import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { RootState } from '@/store/store';

import { PreCostApi, PreCost } from '../models';
import { PaginatedResponse } from '@/types';

import { API_FLEET_URL } from '@/utils';

export interface PreCostsFilter {
  dateFrom?: Date | null;
  dateTo?: Date | null;
  driverId?: string;
  businessId?: string;
  companyId?: string;
}

const preCostsService = createApi({
  reducerPath: 'preCostsService',
  baseQuery: fetchBaseQuery({
    baseUrl: `${API_FLEET_URL}/pre-costs`,
    prepareHeaders: (headers, { getState }) => {
      const state = getState() as RootState;
      const token = state.auth?.auth?.accessToken || null;
      if (token) {
        headers.set('Authorization', `Bearer ${token}`);
      }
      return headers;
    },
  }),
  tagTypes: ['PreCost', 'PreCosts'],
  endpoints: (builder) => ({
    getDriverCost: builder.query<
      PaginatedResponse<PreCost>,
      {
        dateFrom: string;
        dateTo: string;
        driverId?: string;
        businessId: string;
        limit: number;
        offset: number;
      }
    >({
      query: ({ dateFrom, dateTo, driverId, businessId, limit, offset }) => ({
        url: '/drivers',
        params: {
          scheduledFrom: dateFrom || undefined,
          scheduledTo: dateTo || undefined,
          driverId: driverId || undefined,
          commercialBusinessId: businessId || undefined,
          limit,
          offset,
        },
      }),
      transformResponse: (response: PaginatedResponse<PreCost>) => response,
      providesTags: ['PreCost'],
    }),

    getDriverCostTotal: builder.query<
      { totalAmount: number },
      {
        dateFrom: string;
        dateTo: string;
        driverId?: string;
        businessId?: string;
      }
    >({
      query: ({ dateFrom, dateTo, driverId, businessId }) => ({
        url: '/drivers',
        params: {
          scheduledFrom: dateFrom || undefined,
          scheduledTo: dateTo || undefined,
          driverId: driverId || undefined,
          commercialBusinessId: businessId || undefined,
          limit: 10000,
          offset: 0,
        },
      }),
      transformResponse: (response: PaginatedResponse<PreCostApi>) => ({
        totalAmount: response.items.reduce(
          (acc, item) => acc + (item.totalamount || 0),
          0
        ),
      }),
      providesTags: ['PreCosts'],
    }),

    getCompanionCost: builder.query<
      PaginatedResponse<PreCost>,
      {
        dateFrom: string;
        dateTo: string;
        driverId?: string;
        businessId: string;
        companionId: string;
        limit: number;
        offset: number;
      }
    >({
      query: ({
        dateFrom,
        dateTo,
        businessId,
        companionId,
        limit,
        offset,
      }) => ({
        url: '/companions',
        params: {
          scheduledFrom: dateFrom || undefined,
          scheduledTo: dateTo || undefined,
          commercialBusinessId: businessId || undefined,
          companionId: companionId || undefined,
          limit,
          offset,
        },
      }),
      transformResponse: (response: PaginatedResponse<PreCost>) => response,
      providesTags: ['PreCost'],
    }),

    getCompanionCostTotal: builder.query<
      { totalAmount: number },
      {
        dateFrom: string;
        dateTo: string;
        driverId?: string;
        businessId?: string;
        companionId?: string;
      }
    >({
      query: ({ dateFrom, dateTo, businessId, companionId }) => ({
        url: '/companions',
        params: {
          scheduledFrom: dateFrom || undefined,
          scheduledTo: dateTo || undefined,
          commercialBusinessId: businessId || undefined,
          companionId: companionId || undefined,
          limit: 10000,
          offset: 0,
        },
      }),
      transformResponse: (response: PaginatedResponse<PreCostApi>) => ({
        totalAmount: response.items.reduce(
          (acc, item) => acc + (item.companion_totalamount || 0),
          0
        ),
      }),
      providesTags: ['PreCosts'],
    }),
  }),
});

export default preCostsService;

export const {
  useGetDriverCostQuery,
  useGetDriverCostTotalQuery,
  useGetCompanionCostQuery,
  useGetCompanionCostTotalQuery,
} = preCostsService;
